import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from '../services/api';
import log from '../utils/logger';

/**
 * Shared hook for notification state management.
 * Handles loading from localStorage, listening to socket events,
 * and persisting updates. Used by Navbar and Home to avoid duplication.
 */
const useNotifications = (socket) => {
  const [notifications, setNotifications] = useState([]);
  const { isAuthenticated, user } = useAuth0();

  const storageKey = user?.sub ? `notifications_${user.sub}` : null;

  // Load notifications from localStorage on mount
  useEffect(() => {
    if (!isAuthenticated || !storageKey) return;

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
      } catch (error) {
        log.error('Error parsing stored notifications:', error);
        localStorage.removeItem(storageKey);
      }
    }

    // Listen for notifications loaded event from useUserSetup
    const handleNotificationsLoaded = (event) => {
      setNotifications(event.detail.notifications);
    };

    // Listen for localStorage changes from other tabs
    const handleStorageChange = (event) => {
      if (event.key === storageKey) {
        if (event.newValue) {
          try {
            const parsed = JSON.parse(event.newValue);
            setNotifications(parsed);
          } catch (error) {
            log.error('Error parsing notifications from storage event:', error);
          }
        } else {
          setNotifications([]);
        }
      }
    };

    window.addEventListener('notificationsLoaded', handleNotificationsLoaded);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('notificationsLoaded', handleNotificationsLoaded);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, storageKey]);

  // Listen for real-time socket notifications
  useEffect(() => {
    if (!socket || !isAuthenticated || !user) return;

    const userAuthId = user.sub.split('|')[1] || user.sub;
    socket.emit('userJoined', userAuthId);

    const handleWebNotification = (notification) => {
      setNotifications((prev) => {
        const updated = [...prev, notification];
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        }
        return updated;
      });
    };

    socket.on('webNotification', handleWebNotification);

    return () => {
      socket.off('webNotification', handleWebNotification);
    };
  }, [socket, isAuthenticated, user, storageKey]);

  // Clear all notifications
  const clearNotifications = useCallback(async () => {
    const notificationIds = notifications.map(n => n.id);
    if (notificationIds.length > 0) {
      try {
        await axios.patch('/notifications/mark-delivered', { notificationIds });
      } catch (error) {
        log.error('Error marking notifications as delivered:', error);
      }
    }
    setNotifications([]);
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }, [notifications, storageKey]);

  // Update notifications with a transform function (for removing/marking individual items)
  const updateNotifications = useCallback((updateFn) => {
    setNotifications(prev => {
      const updated = updateFn(prev);
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      }
      return updated;
    });
  }, [storageKey]);

  return { notifications, setNotifications, clearNotifications, updateNotifications };
};

export default useNotifications;
